// Background removal tool using Transformers.js with Xenova/modnet model.
//
// This tool removes the background from images, returning a transparent PNG.
// The model is loaded on-demand and cached for subsequent uses.
//

import type { ImageSegmentationPipeline } from "@huggingface/transformers";

// Singleton instance of the segmenter pipeline.
//
let segmenter: ImageSegmentationPipeline | null = null;

// Progress callback type for model loading.
//
export type ProgressCallback = (progress: {
  status: string;
  progress?: number;
  loaded?: number;
  total?: number;
}) => void;

interface RawImagePixels {
  data: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
}

function getCanvas2DContext(
  canvas: HTMLCanvasElement,
): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  return ctx;
}

// Initializes the background removal model.
//
// This loads the Xenova/modnet model which is optimized for portrait/people
// images. The model is approximately 7MB and will be cached in IndexedDB after
// the first load.
//
export async function initializeModel(
  onProgress?: ProgressCallback,
): Promise<void> {
  if (segmenter) {
    return;
  }

  const { env, pipeline } = await import("@huggingface/transformers");
  env.allowLocalModels = false;

  segmenter = await pipeline("image-segmentation", "Xenova/modnet", {
    progress_callback: onProgress,
  });
}

// Removes the background from an image.
//
// Takes an image URL or data URL and returns a Blob containing a transparent
// PNG with the background removed.
//
export async function removeBackground(imageSource: string): Promise<Blob> {
  if (!segmenter) {
    await initializeModel();
  }

  // Process the image through the segmentation pipeline.
  //
  const activeSegmenter = segmenter;
  if (!activeSegmenter) {
    throw new Error("Background removal model failed to initialize");
  }

  const result = await activeSegmenter(imageSource);

  // The result contains the mask. We need to apply it to the original image.
  //
  if (!result || result.length === 0) {
    throw new Error("Failed to process image");
  }

  // Load the original image to get its dimensions.
  //
  const { RawImage } = await import("@huggingface/transformers");
  const originalImage = (await RawImage.fromURL(
    imageSource,
  )) as unknown as RawImagePixels;

  // Get the mask from the result.
  //
  const firstSegment = result[0];
  if (!firstSegment) {
    throw new Error("Failed to process image");
  }

  const maskImage = firstSegment.mask as unknown as RawImagePixels;

  // Create a canvas to composite the result.
  //
  const canvas = document.createElement("canvas");
  canvas.width = originalImage.width;
  canvas.height = originalImage.height;

  const ctx = getCanvas2DContext(canvas);

  // Draw the original image.
  //
  const imageData = ctx.createImageData(
    originalImage.width,
    originalImage.height,
  );

  // Copy original image data.
  //
  for (let i = 0; i < originalImage.data.length; i++) {
    imageData.data[i] = originalImage.data[i];
  }

  // Apply the mask to the alpha channel.
  //
  // The mask values are 0-255 where 255 is foreground (keep) and 0 is
  // background (remove).
  //
  for (let i = 0; i < maskImage.data.length; i++) {
    imageData.data[i * 4 + 3] = maskImage.data[i];
  }

  ctx.putImageData(imageData, 0, 0);

  // Convert canvas to blob.
  //
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob from canvas"));
        }
      },
      "image/png",
      1.0,
    );
  });
}

// Checks if the model is already loaded.
//
export function isModelLoaded(): boolean {
  return segmenter !== null;
}
